/**
 * NIRSALDashboard — Green Credit & Carbon Asset Dashboard
 * Route: /corporate/nirsal/dashboard
 * Data: NIRSALContext (SSOT)
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { TrendingUp, Shield, Leaf, Users, MapPin, Building2, BarChart2, Zap } from "lucide-react";
import NIRSALIntelligencePanel from "./NIRSALIntelligencePanel";

const fmt = (v, u = "") => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M${u}`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K${u}`;
  return `${n.toLocaleString()}${u}`;
};
const fmtN = (v, u = "") => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ${u}`.trim();
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K ${u}`.trim();
  return `${n.toLocaleString()} ${u}`.trim();
};

const KPICard = ({ icon: Icon, label, value, sub, color = "#059669" }) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color + "18" }}>
      <Icon size={18} style={{ color }} />
    </div>
    <div>
      <p className="text-xs text-slate-400 font-medium">{label}</p>
      <p className="text-xl font-black text-slate-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const PathwayBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round(value / total * 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className="text-slate-500">{fmtN(value, "tCO₂e/yr")} · {pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: pct + "%", background: color }} />
      </div>
    </div>
  );
};

export default function NIRSALDashboard() {
  const { consolidated, loading, periods, selectedPeriod, setSelectedPeriod } = useNIRSAL();
  const navigate = useNavigate();
  const p = consolidated?.portfolio || {};
  const total = p.total_reduction_tco2e_yr || 0;

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading dashboard…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-[#F8FAFC] min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider text-[#1e3a5f] bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">NIRSAL Plc</span>
            <span className="text-xs text-slate-400">National Green Credit & Carbon Asset Program</span>
          </div>
          <PageHeader label="NIRSAL GREEN CREDIT & CARBON ASSET DASHBOARD" title="" />
          <p className="text-xs text-slate-400 mt-0.5">One national PoA · CPAs by state/commodity/technology · CRG-backed climate finance</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedPeriod || ""}
            onChange={e => setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none"
            data-testid="period-selector"
          >
            {periods.map(pp => <option key={pp.period_id} value={pp.period_id}>{pp.label}</option>)}
          </select>
          <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-right">
            <p className="text-xs font-bold text-[#1e3a5f]">Portfolio Readiness</p>
            <p className="text-lg font-black text-emerald-600">{consolidated?.readiness_score || "—"}<span className="text-xs font-normal text-slate-400">/100</span></p>
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard icon={Leaf} label="Total GHG Reduction" value={fmtN(total, "tCO₂e/yr")} sub="Scope 1+2 portfolio" color="#059669" />
        <KPICard icon={TrendingUp} label="Capital Mobilized" value={fmt(p.total_capital_mobilized)} sub={`${p.total_clusters || 0} clusters`} color="#1e3a5f" />
        <KPICard icon={Shield} label="CRG Exposure" value={fmt(p.total_crg_exposure)} sub={`${(p.leverage_ratio || 0).toFixed(1)}x leverage`} color="#7c3aed" />
        <KPICard icon={Users} label="Beneficiaries" value={fmtN(p.total_beneficiaries)} sub={`${p.total_jobs_created || 0} jobs created`} color="#d97706" />
        <KPICard icon={MapPin} label="States Active" value={p.states_count || 0} sub={(p.states_active || []).slice(0,3).join(", ")} color="#0891b2" />
        <KPICard icon={Building2} label="PFIs Active" value={p.pfis_count || 0} sub={(p.pfis_active || []).slice(0,2).join(", ")} color="#64748b" />
        <KPICard icon={BarChart2} label="Carbon Value (Base)" value={fmt(p.carbon_value_base_usd, "/yr")} sub="Indicative — conditional" color="#059669" />
        <KPICard icon={Zap} label="Plastic Value" value={fmt(p.plastic_value_usd, "/yr")} sub="Plastic credits — indicative" color="#8b5cf6" />
      </div>

      {/* Pathway Breakdown + Top Clusters */}
      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="Carbon Pathway Split" className="col-span-1">
          <div className="p-4">
            <PathwayBar label="Article 6.4" value={p.art6_4_reduction || 0} total={total} color="#1e3a5f" />
            <PathwayBar label="VCM" value={p.vcm_reduction || 0} total={total} color="#059669" />
            <PathwayBar label="Plastic Credits" value={p.plastic_reduction || 0} total={total} color="#8b5cf6" />
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
              <p>Art.6.4: {p.art6_4_clusters || 0} clusters · VCM: {p.vcm_clusters || 0} · Plastic: {p.plastic_clusters || 0}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Top Clusters by Score" className="col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 px-3 text-slate-400 font-medium">Cluster</th>
                  <th className="text-left py-2 px-3 text-slate-400 font-medium">State</th>
                  <th className="text-left py-2 px-3 text-slate-400 font-medium">Reduction</th>
                  <th className="text-left py-2 px-3 text-slate-400 font-medium">Pathway</th>
                  <th className="text-left py-2 px-3 text-slate-400 font-medium">Status</th>
                  <th className="text-right py-2 px-3 text-slate-400 font-medium">Score</th>
                </tr>
              </thead>
              <tbody>
                {(consolidated?.opportunities || []).slice(0, 5).map(o => (
                  <tr key={o.opp_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium text-slate-800 max-w-[180px] truncate">{o.name}</td>
                    <td className="py-2 px-3 text-slate-500">{o.state}</td>
                    <td className="py-2 px-3 text-emerald-700 font-bold">{fmtN(o.reduction_tco2e_yr, "tCO₂e/yr")}</td>
                    <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700">{o.pathway_label}</span></td>
                    <td className="py-2 px-3 capitalize text-slate-500">{o.status}</td>
                    <td className="py-2 px-3 text-right font-black text-[#1e3a5f]">{(o.composite_score || 0).toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-100 flex gap-2">
            <button onClick={() => navigate("/corporate/nirsal/value-bridge")} className="text-xs text-blue-600 hover:underline" data-testid="view-value-bridge">View Value Bridge →</button>
            <span className="text-slate-300">·</span>
            <button onClick={() => navigate("/corporate/nirsal/carbon-structuring")} className="text-xs text-blue-600 hover:underline">Carbon Structuring →</button>
          </div>
        </SectionCard>
      </div>

      {/* By-State Breakdown */}
      {p.by_state && Object.keys(p.by_state).length > 0 && (
        <SectionCard title="Portfolio by State">
          <div className="p-4 grid grid-cols-5 gap-3">
            {Object.entries(p.by_state).map(([state, d]) => (
              <div key={state} className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xs font-bold text-[#1e3a5f]">{state}</p>
                <p className="text-sm font-black text-emerald-600 mt-1">{fmtN(d.reduction, "tCO₂e")}</p>
                <p className="text-[10px] text-slate-400">{d.count} cluster{d.count !== 1 ? "s" : ""}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* National Intelligence Layer */}
      {selectedPeriod && <NIRSALIntelligencePanel selectedPeriod={selectedPeriod} />}
    </div>
  );
}
